import {
    autoUpdate,
    FloatingFocusManager,
    size,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
    useRole
} from '@floating-ui/react';
import { faChevronDown, faUsers } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useAuth from '@hooks/useAuth';
import { FC, Key, useState } from 'react';
import { flushSync } from 'react-dom';
import useSWR from 'swr';

interface CompactMember {
    name: string;
    number: string;
    id: Key;
}

interface Props {
    onSelect?: (member: CompactMember) => void;
    clubSlug?: string;
}

const MemberSelection: FC<React.PropsWithChildren<React.PropsWithChildren<Props>>> = ({ onSelect, clubSlug }) => {
    const { isAuthenticated } = useAuth();
    const { data } = useSWR<{ data: CompactMember[] }>(
        isAuthenticated && `user/members${clubSlug ? `/${clubSlug}` : ''}`,
        null,
        {
            onSuccess: ({ data }) => {
                if (data?.length > 0) onSelect?.(data[0]);
            }
        }
    );
    const membersExist = data?.data?.length > 0;
    const [isOpen, setIsOpen] = useState(false);
    const [maxHeight, setMaxHeight] = useState(null);
    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        middleware: [
            size({
                apply({ availableHeight }) {
                    flushSync(() => setMaxHeight(availableHeight));
                }
            })
        ],
        strategy: 'fixed',
        whileElementsMounted: autoUpdate,
        placement: 'bottom-end'
    });

    const click = useClick(context, {
        enabled: membersExist
    });
    const dismiss = useDismiss(context);
    const role = useRole(context);

    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

    return (
        <div ref={refs.setReference} {...getReferenceProps()}>
            {membersExist && (
                <button
                    type="button"
                    className="tw-flex tw-gap-1 tw-border-none tw-bg-transparent tw-p-2 tw-py-3 tw-text-accents-6"
                >
                    <FontAwesomeIcon icon={faUsers} />
                    <FontAwesomeIcon icon={faChevronDown} />
                </button>
            )}
            {isOpen && (
                <FloatingFocusManager context={context} modal={false}>
                    <div
                        ref={refs.setFloating}
                        style={{ ...floatingStyles, maxHeight: maxHeight - 12 }}
                        className="tw-z-[999] tw-min-w-min tw-max-w-[300px] tw-overflow-y-auto tw-rounded-lg tw-border tw-border-solid tw-border-accents-0 tw-bg-white tw-px-4 tw-py-2 tw-shadow-lg"
                        {...getFloatingProps()}
                    >
                        {data?.data.map((member) => (
                            <div
                                key={member.id}
                                className="tw-flex tw-cursor-pointer tw-justify-between tw-gap-8 tw-rounded-lg tw-p-1 tw-px-2 hover:tw-bg-accents-2"
                                onClick={() => onSelect?.(member)}
                            >
                                <span className="tw-block tw-overflow-hidden tw-overflow-ellipsis tw-font-semibold">
                                    {member.name}
                                </span>
                                <span className="tw-flex-1 tw-text-end tw-text-accents-6">{member.number}</span>
                            </div>
                        ))}
                    </div>
                </FloatingFocusManager>
            )}
        </div>
    );
};

export default MemberSelection;
